'use client';

import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Button, { buttonClasses } from '@mui/material/Button';

import { _socials, cardsList, getUIMode } from 'src/utils/helper';

import { Logo } from 'src/components/logo';
import { Iconify } from 'src/components/iconify';
import { useLocale } from 'src/locales';
import { useEffect, useState } from 'react';
import { UserDeleteModal } from 'src/sections/delete-account/modal/user-delete-modal';
import { UserDeleteOTPModal } from 'src/sections/delete-account/modal/user-delete-otp-modal';
import { CONFIG } from 'src/global-config';
import STCPay from 'src/assets/illustrations/StcPay';
import Madacard from 'src/assets/illustrations/Madacard';

// ----------------------------------------------------------------------

export function Footer({ layoutQuery = 'md', sx, ...other }) {
  const { t } = useLocale();
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [deleteUserOTPModal, setDeleteUserOTPModal] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');

  useEffect(() => {
    if (window.location.hash === '#delete-account') {
      setDeleteUserModal(true);
    }
  }, []);

  const renderInfo = () => (
    <>
      <Box
        component="img"
        alt="Logo"
        sx={{ width: { md: 100, xs: 60 }, display: { md: 'block', xs: 'none' } }}
        src={`${CONFIG.assetsDir}/assets/core/shahen-logo-cropped.svg`}
      />
      <Typography variant="body2" sx={{ maxWidth: 360, color: 'text.secondary' }}>
        {t('Footer_Description')}
      </Typography>
    </>
  );

  const renderCommunity = () => (
    <>
      <Typography variant="subtitle2">{t('legal_info')}</Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {t('National_Unified_Number')} : 7015321933
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {t('VAT_Number')} : 310380242500003
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {t('TGA_License')} : 29/00000747
      </Typography>
    </>
  );

  const renderSubscribe = () => (
    <>
      <div>
        <Typography variant="h6" component="h6" sx={{ mb: 1 }}>
          Let’s stay in touch
        </Typography>
        <Typography
          variant="caption"
          sx={{ maxWidth: 360, display: 'block', color: 'text.secondary' }}
        >
          Subscribe to our newsletter to receive latest articles to your inbox weekly.
        </Typography>
      </div>

      <TextField
        fullWidth
        hiddenLabel
        placeholder="Email address"
        sx={{ maxWidth: 420 }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Button variant="contained" color="inherit" size="large" sx={{ mr: -1.25 }}>
                  Subscribe
                </Button>
              </InputAdornment>
            ),
          },
        }}
      />
    </>
  );

  const renderSocials = () => (
    <>
      <Typography variant="h6" component="h6">
        {t('Follow_Us')}
      </Typography>

      <Box sx={{ display: 'flex' }}>
        {_socials.map((social) => (
          <IconButton
            key={social.label}
            onClick={() => window.open(social.url, '_blank')}
            sx={{ color: 'text.secondary' }}
            aria-label={social.label}
          >
            {social.value === 'twitter' && <Iconify icon="socials:twitter" />}
            {social.value === 'facebook' && <Iconify icon="socials:facebook" />}
            {social.value === 'instagram' && <Iconify icon="socials:instagram" />}
            {social.value === 'linkedin' && <Iconify icon="socials:linkedin" />}
            {social.value === 'youtube' && <Iconify icon="logos:youtube-icon" />}
          </IconButton>
        ))}
      </Box>
    </>
  );
  const renderPaymentMethods = () => (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {cardsList.map((social, index) =>
        social.logo ? (
          <Iconify
            icon={social.logo}
            width={30}
            key={index}
            fill={getUIMode() === 'dark' ? '#FFFFFF' : ''}
          />
        ) : social.component == 'mada' ? (
          <Madacard key={index} />
        ) : (
          <STCPay key={index} />
        )
      )}
    </Box>
  );

  const renderApps = () => (
    <>
      <Typography variant="h6" component="h6">
        {t('Apps')}
      </Typography>
      <Box
        sx={{
          gap: 2,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <AppStoreButton
          onClick={() =>
            window.open(
              'https://apps.apple.com/sa/app/shahen-%D8%B4%D8%AD%D9%86/id1568118147',
              '_blank'
            )
          }
          startIcon={<Iconify width={28} icon="custom:apple" />}
          caption="Download on the"
          title="Apple Store"
        />
        <AppStoreButton
          onClick={() =>
            window.open('https://play.google.com/store/apps/details?id=com.shahen&gl=SA', '_blank')
          }
          startIcon={<Iconify width={28} icon="custom:google-play" />}
          caption="Download from"
          title="Google Play"
        />
      </Box>
    </>
  );

  // ✅ Static navigation only
  const renderList = () => (
    <Box
      component="ul"
      sx={(theme) => ({
        columnGap: 2,
        // display: 'none',
        columnCount: { xs: 2, lg: 3 },
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'block',
        },
      })}
    >
      <Box
        component="li"
        sx={{
          mb: 2,
          gap: 0.75,
          display: 'flex',
          breakInside: 'avoid',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="subtitle2">{t('Quick_Links')}</Typography>
        <Box component="ul" sx={{ gap: 'inherit', display: 'flex', flexDirection: 'column' }}>
          <Box component="li">
            <Link href={`/`} variant="caption" sx={{ color: 'text.secondary' }}>
              {t('Home')}
            </Link>
          </Box>
          <Box component="li">
            <Link
              href={`${CONFIG.assetsDir}/business`}
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {t('Business')}
            </Link>
          </Box>
          <Box component="li">
            <Link
              href={`${CONFIG.assetsDir}/about`}
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {t('About_us')}
            </Link>
          </Box>
          <Box component="li">
            <Link
              href={`${CONFIG.assetsDir}/faqs`}
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {t('FAQs')}
            </Link>
          </Box>

          <Box component="li">
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
              }}
              onClick={() => {
                setDeleteUserModal(true);
                window.location.hash = 'delete-account';
              }}
            >
              {t('Delete_User_Account')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component="li"
        sx={{
          mb: 2,
          gap: 0.75,
          display: 'flex',
          breakInside: 'avoid',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="subtitle2">{t('Legal_Details')}</Typography>
        <Box component="ul" sx={{ gap: 'inherit', display: 'flex', flexDirection: 'column' }}>
          <Box component="li">
            <Link
              href={`${CONFIG.assetsDir}/privacy-policy`}
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {t('Privacy_Policy')}
            </Link>
          </Box>
          <Box component="li">
            <Link
              href={`${CONFIG.assetsDir}/terms-and-conditions`}
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {t('Terms_of_Service')}
            </Link>
          </Box>
        </Box>
      </Box>

      <Box
        component="li"
        sx={{
          mb: 2,
          gap: 0.75,
          display: 'flex',
          breakInside: 'avoid',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="subtitle2">{t('Contact_Us')}</Typography>
        <Box component="ul" sx={{ gap: 'inherit', display: 'flex', flexDirection: 'column' }}>
          <Link
            href={'mailto:info@shahen-sa.com'}
            variant="caption"
            sx={{ color: 'text.secondary' }}
          >
            {t('Email')} : info@shahen-sa.com
          </Link>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}></Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {t('Phone')} : 920012163
          </Typography>
        </Box>
      </Box>
      <Box
        component="li"
        sx={{
          mb: 2,
          gap: 0.75,
          display: 'flex',
          breakInside: 'avoid',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="subtitle2">{t('The_Address')}</Typography>
        <Box component="ul" sx={{ gap: 'inherit', display: 'flex', flexDirection: 'column' }}>
          <Box component="li">
            <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Al Muhandis Masaid Al Anqari, As Sulimaniyah, Riyadh 12245
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        component="li"
        sx={{
          mb: 2,
          gap: 0.75,
          display: 'flex',
          breakInside: 'avoid',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {renderCommunity()}
      </Box>
    </Box>
  );

  const renderTop = () => (
    <Container
      sx={{
        pt: { xs: 5, md: 5 },
        pb: { xs: 5, md: 5 },
      }}
    >
      <Grid container spacing={1} sx={{ justifyContent: { md: 'space-between' } }}>
        <Grid
          size={{ xs: 12, md: 5, lg: 4 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, md: 5 },
          }}
        >
          <Box sx={[(theme) => ({ ...blockStyles(theme, layoutQuery) }), { gap: 3 }]}>
            {renderInfo()}
          </Box>
        </Grid>

        <Grid component="nav" size={{ xs: 12, md: 6, lg: 7 }}>
          {renderList()}
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // ✅ stack on mobile, row on larger screens
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          // alignItems: { xs: 'center', sm: 'center' },
          gap: { xs: 4, sm: 2 }, // ✅ spacing between sections on mobile
          mt: 2,
        }}
      >
        <Box sx={[(theme) => ({ ...blockStyles(theme, layoutQuery), flex: 5 })]}>
          {renderSocials()}
        </Box>
        <Box sx={[(theme) => ({ ...blockStyles(theme, layoutQuery), flex: 7 })]}>
          {renderApps()}
        </Box>
      </Box>
    </Container>
  );

  const renderBottom = () => (
    <Container
      sx={{
        py: 3,
        gap: 2.5,
        display: 'flex',
        textAlign: 'center',
        color: 'text.secondary',
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
      }}
    >
      <Typography variant="caption"> {t('All_rights_reserved')}</Typography>

      <Box
        component="span"
        sx={{
          gap: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {renderPaymentMethods()}
      </Box>

      <UserDeleteModal
        open={deleteUserModal}
        onClose={() => {
          setDeleteUserModal(false);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }}
        setDeleteUserOTPModal={setDeleteUserOTPModal}
        setMobileNumber={setMobileNumber}
      />
      <UserDeleteOTPModal
        open={deleteUserOTPModal}
        onClose={() => setDeleteUserOTPModal(!deleteUserOTPModal)}
        mobileNumber={mobileNumber}
      />
    </Container>
  );

  return (
    <Box
      component="footer"
      sx={[
        (theme) => ({
          borderTop: `solid 1px ${theme.vars.palette.divider}`,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {renderTop()}
      <Divider />
      {renderBottom()}
    </Box>
  );
}

// ----------------------------------------------------------------------

const blockStyles = (theme, layoutQuery) => ({
  gap: 2,
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  [theme.breakpoints.up(layoutQuery)]: {
    textAlign: 'left',
    alignItems: 'flex-start',
  },
});

// ----------------------------------------------------------------------

const AppStoreButton = styled(({ title, caption, ...other }) => (
  <Button {...other}>
    <div>
      <Box
        component="span"
        sx={{
          opacity: 0.72,
          display: 'block',
          textAlign: 'left',
          typography: 'caption',
        }}
      >
        {caption}
      </Box>

      <Box component="span" sx={{ mt: -0.5, typography: 'h6' }}>
        {title}
      </Box>
    </div>
  </Button>
))(({ theme }) => ({
  flexShrink: 0,
  padding: '5px 12px',
  color: theme.vars.palette.common.white,
  border: `solid 1px ${varAlpha(theme.vars.palette.common.blackChannel, 0.24)}`,
  background: `linear-gradient(180deg, ${theme.vars.palette.grey[900]}, ${theme.vars.palette.common.black})`,
  [`& .${buttonClasses.startIcon}`]: {
    marginLeft: 0,
  },
}));
